import React, { useEffect, useMemo, useState } from "react";
import { apiGetJson, clamp, fmtDate } from "../gemv2/gemV2Api";
import Layout from "../Layout";

const COLORS = {
  text: "#1f2937",
  muted: "#6b7280",
  border: "#e5e7eb",
  border2: "#f3f4f6",
  bgHead: "#f9fafb",
  link: "#2563eb",
};

const containerStyle = { maxWidth: 1400, margin: "0 auto", padding: 16, color: COLORS.text };
const cardStyle = { border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12, background: "#fff" };
const rowStyle = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" };
const inputStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #d1d5db", minWidth: 320 };
const btnStyle = { padding: "9px 12px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" };
const smallMuted = { color: COLORS.muted, fontSize: 12 };
const tableWrap = { overflowX: "auto", borderRadius: 12, border: `1px solid ${COLORS.border}` };
const thStyle = {
  textAlign: "left",
  padding: 10,
  background: COLORS.bgHead,
  borderBottom: `1px solid ${COLORS.border}`,
  whiteSpace: "nowrap",
  verticalAlign: "top",
  lineHeight: 1.2,
};
const tdStyle = { padding: 10, borderBottom: `1px solid ${COLORS.border2}`, verticalAlign: "top" };
const headerFilterInputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "6px 7px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
};
const headerFilterDateStyle = { ...headerFilterInputStyle, padding: "4px 6px" };

const COLUMNS = [
  { key: "TenderID", label: "Tender ID", kind: "text", sortField: "TenderID", filterKey: "f_TenderID" },
  { key: "TitleRefNo", label: "Title and Ref.No.", kind: "text", sortField: "TitleRefNo", filterKey: "f_TitleRefNo" },
  { key: "OrganisationChain", label: "Organisation Chain", kind: "text", sortField: "OrganisationChain", filterKey: "f_OrganisationChain" },
  { key: "AwardedBidder", label: "Awarded Bidder", kind: "text", sortField: "AwardedBidder", filterKey: "f_AwardedBidder" },
  { key: "AwardedValue", label: "Awarded Value", kind: "numeric", sortField: "AwardedValue", filterKey: "minAwardedValue" },
  { key: "AOCLink", label: "AOC Link", kind: "link" },
  { key: "BOQLink", label: "BOQ Link", kind: "link" },
  { key: "SummaryLink", label: "Summary Link", kind: "link" },
  { key: "AOCContractDate", label: "AOC Contract Date", kind: "date", sortField: "AOCContractDate", dateFromKey: "aocDateFrom", dateToKey: "aocDateTo" },
  { key: "AOCContractValue", label: "AOC Contract Value", kind: "numeric", sortField: "AOCContractValue", filterKey: "minAOCContractValue" },
  { key: "AOCUpdatedOn", label: "AOC Updated On", kind: "date", sortField: "AOCUpdatedOn", dateFromKey: "aocUpdatedFrom", dateToKey: "aocUpdatedTo" },
  { key: "Portal", label: "Portal", kind: "portal", sortField: "PortalSource", filterKey: "f_PortalSource" },
];

function useDebouncedValue(value, delayMs = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function Pagination({ page, totalPages, onPage, loading }) {
  const safeTotal = Math.max(1, totalPages || 1);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <button style={btnStyle} onClick={() => onPage(clamp(page - 1, 1, safeTotal))} disabled={page <= 1 || loading}>Prev</button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={smallMuted}>Page</span>
        <input
          style={{ ...inputStyle, minWidth: 90, width: 90 }}
          value={page}
          onChange={(e) => {
            const v = parseInt(e.target.value || "1", 10);
            onPage(clamp(Number.isNaN(v) ? 1 : v, 1, safeTotal));
          }}
        />
        <span style={smallMuted}>of {safeTotal}</span>
        {loading ? <span className="tm-spinner" title="Loading" /> : null}
      </div>
      <button style={btnStyle} onClick={() => onPage(clamp(page + 1, 1, safeTotal))} disabled={page >= safeTotal || loading}>Next</button>
    </div>
  );
}

function SortHeader({ label, field, sortField, sortOrder, onSort, loading }) {
  const sortable = Boolean(field);
  const active = sortField === field;
  const arrow = sortable ? (active ? (sortOrder === "ASC" ? " \u25B2" : " \u25BC") : " \u2195") : "";
  return (
    <th
      style={{
        ...thStyle,
        cursor: sortable && !loading ? "pointer" : "default",
        opacity: loading ? 0.7 : 1,
        fontWeight: active ? 800 : 700,
      }}
      onClick={() => sortable && !loading && onSort(field)}
      title={sortable ? "Click to sort" : ""}
    >
      {label}{sortable ? <span style={{ color: active ? COLORS.text : "#9ca3af" }}>{arrow}</span> : null}
    </th>
  );
}

function escapeRegExp(str = "") {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getSearchTokens(search = "") {
  return search.split(",").map((s) => s.trim()).filter(Boolean);
}

function HighlightText({ text: rawText, search, filterTerm }) {
  const raw = rawText == null ? "" : String(rawText);
  const tokens = [
    ...getSearchTokens(search),
    ...getSearchTokens(filterTerm),
  ];
  if (!raw || tokens.length === 0) return <>{raw || "\u2014"}</>;

  const pattern = tokens.map(escapeRegExp).join("|");
  if (!pattern) return <>{raw || "\u2014"}</>;

  const regex = new RegExp(`(${pattern})`, "ig");
  const parts = raw.split(regex);

  return (
    <>
      {parts.map((part, idx) => {
        const isMatch = tokens.some((t) => t.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <mark key={idx} style={{ backgroundColor: "#fef08a", padding: "0 1px" }}>{part}</mark>
        ) : (
          <React.Fragment key={idx}>{part}</React.Fragment>
        );
      })}
    </>
  );
}

function getPortalDisplay(r) {
  if (r?.PortalFolderKey) return r.PortalFolderKey;
  if (r?.PortalSource) return r.PortalSource;
  if (r?.PortalLink) {
    try {
      const u = new URL(r.PortalLink);
      const seg = u.pathname.split("/").filter(Boolean)[0];
      if (seg) return seg;
      return u.hostname.replace(/^www\./i, "");
    } catch (_) { return "Portal"; }
  }
  return "\u2014";
}

export default function CpppV2ResultsPage() {
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortField, setSortField] = useState("AOCContractDate");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [filters, setFilters] = useState({
    f_TenderID: "",
    f_TitleRefNo: "",
    f_OrganisationChain: "",
    f_AwardedBidder: "",
    f_PortalSource: "",
    minAwardedValue: "",
    minAOCContractValue: "",
    aocDateFrom: "",
    aocDateTo: "",
    aocUpdatedFrom: "",
    aocUpdatedTo: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resp, setResp] = useState({ total: 0, data: [], totalPages: 1 });

  const search = useDebouncedValue(searchInput.trim(), 300);
  const debouncedFilters = useDebouncedValue(filters, 350);

  useEffect(() => {
    setPage(1);
  }, [search, debouncedFilters, limit, sortField, sortOrder]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("status", "archive");
    p.set("page", String(page));
    p.set("limit", String(limit));
    p.set("search", search.trim());
    p.set("sortField", sortField);
    p.set("sortOrder", sortOrder === "ASC" ? "asc" : "desc");
    // Column filters
    Object.entries(debouncedFilters).forEach(([k, v]) => {
      if (v) p.set(k, v);
    });
    return `?${p.toString()}`;
  }, [page, limit, search, sortField, sortOrder, debouncedFilters]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const json = await apiGetJson(`/api/cppp-v2/results${query}`, 'results');
        if (!mounted) return;
        setResp(json);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [query]);

  function onSort(field) {
    if (sortField === field) {
      setSortOrder((s) => (s === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
  }

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearAllFilters() {
    setSearchInput("");
    setFilters({
      f_TenderID: "", f_TitleRefNo: "", f_OrganisationChain: "", f_AwardedBidder: "", f_PortalSource: "",
      minAwardedValue: "", minAOCContractValue: "",
      aocDateFrom: "", aocDateTo: "", aocUpdatedFrom: "", aocUpdatedTo: "",
    });
    setPage(1);
  }

  const rows = resp.data || [];

  return (
    <Layout>
      <div style={containerStyle}>
        <style>{`
          .tm-loadingbar-wrap{
            height: 3px; border-radius: 999px; background: #eef2ff;
            overflow: hidden; margin-top: 10px;
          }
          .tm-loadingbar{
            height: 100%; width: 40%; background: #111827;
            transform: translateX(-120%); animation: tmbar 1.1s infinite;
          }
          @keyframes tmbar{
            0%{ transform: translateX(-120%); opacity: .35; }
            50%{ opacity: .9; }
            100%{ transform: translateX(260%); opacity: .35; }
          }
          .tm-spinner{
            width: 14px; height: 14px; border-radius: 999px;
            border: 2px solid #e5e7eb; border-top-color: #111827;
            animation: tmspin .8s linear infinite; display: inline-block;
          }
          @keyframes tmspin{
            from{ transform: rotate(0deg); } to{ transform: rotate(360deg); }
          }
        `}</style>

        <div style={{ ...cardStyle, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, display: "flex", gap: 10, alignItems: "center" }}>
                <span>Non Gem Portal Results (CPPP etc.)</span>
                {loading ? <span className="tm-spinner" title="Loading" /> : null}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 800 }}>{(resp.total || 0).toLocaleString()} tenders</div>
            </div>
          </div>

          {loading ? (
            <div className="tm-loadingbar-wrap" title="Loading"><div className="tm-loadingbar" /></div>
          ) : null}

          <div style={{ marginTop: 12, ...rowStyle }}>
            <input
              style={inputStyle}
              placeholder="Search: Tender ID, Title, Organisation, Bidder, Portal..."
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            />

            <button style={btnStyle} onClick={clearAllFilters}>Clear Filters</button>

            <div style={{ marginLeft: "auto" }}>
              <Pagination page={page} totalPages={resp.totalPages} onPage={setPage} loading={loading} />
            </div>
          </div>

          {error ? <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>Error: {error}</div> : null}
        </div>

        <div style={tableWrap}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <SortHeader key={col.key} label={col.label} field={col.sortField} sortField={sortField} sortOrder={sortOrder} onSort={onSort} loading={loading} />
                ))}
              </tr>
              <tr>
                {COLUMNS.map((col) => {
                  const baseTh = { ...thStyle, background: "#fff", padding: 6, whiteSpace: "normal" };

                  if (col.kind === "date") {
                    return (
                      <th key={`${col.key}_filter`} style={baseTh}>
                        <div style={{ display: "grid", gap: 4 }}>
                          <input type="date" style={headerFilterDateStyle} value={filters[col.dateFromKey] || ""} onChange={(e) => setFilter(col.dateFromKey, e.target.value)} title="From date" />
                          <input type="date" style={headerFilterDateStyle} value={filters[col.dateToKey] || ""} onChange={(e) => setFilter(col.dateToKey, e.target.value)} title="To date" />
                        </div>
                      </th>
                    );
                  }

                  if (col.kind === "numeric" && col.filterKey) {
                    return (
                      <th key={`${col.key}_filter`} style={baseTh}>
                        <input
                          type="number"
                          step="0.01"
                          style={headerFilterInputStyle}
                          placeholder="Min"
                          value={filters[col.filterKey] || ""}
                          onChange={(e) => setFilter(col.filterKey, e.target.value)}
                        />
                      </th>
                    );
                  }

                  if ((col.kind === "text" || col.kind === "portal") && col.filterKey) {
                    return (
                      <th key={`${col.key}_filter`} style={baseTh}>
                        <input
                          style={headerFilterInputStyle}
                          placeholder="Filter"
                          value={filters[col.filterKey] || ""}
                          onChange={(e) => setFilter(col.filterKey, e.target.value)}
                        />
                      </th>
                    );
                  }

                  return <th key={`${col.key}_filter`} style={baseTh} />;
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const portalText = getPortalDisplay(r);
                return (
                  <tr key={`${r.RowId || "x"}_${i}`}>
                    <td style={tdStyle}><HighlightText text={r.TenderID} search={search} filterTerm={debouncedFilters.f_TenderID} /></td>
                    <td style={tdStyle}><HighlightText text={r.TitleRefNo} search={search} filterTerm={debouncedFilters.f_TitleRefNo} /></td>
                    <td style={tdStyle}><HighlightText text={r.OrganisationChain} search={search} filterTerm={debouncedFilters.f_OrganisationChain} /></td>

                    <td style={tdStyle}><HighlightText text={r.AwardedBidder} search={search} filterTerm={debouncedFilters.f_AwardedBidder} /></td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{r.AwardedValue != null ? Number(r.AwardedValue).toLocaleString() : "\u2014"}</td>

                    <td style={tdStyle}>
                      {r.AOCLink ? (
                        <a href={r.AOCLink} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>Open</a>
                      ) : "\u2014"}
                    </td>
                    <td style={tdStyle}>
                      {r.BOQLink ? (
                        <a href={r.BOQLink} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>Open</a>
                      ) : "\u2014"}
                    </td>
                    <td style={tdStyle}>
                      {r.SummaryLink ? (
                        <a href={r.SummaryLink} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>Open</a>
                      ) : "\u2014"}
                    </td>
                    <td style={tdStyle}>{fmtDate(r.AOCContractDate)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{r.AOCContractValue != null ? Number(r.AOCContractValue).toLocaleString() : "\u2014"}</td>
                    <td style={tdStyle}>{fmtDate(r.AOCUpdatedOn)}</td>
                    <td style={tdStyle}>
                      {r.PortalLink ? (
                        <a href={r.PortalLink} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>
                          <HighlightText text={portalText} search={search} filterTerm={debouncedFilters.f_PortalSource} />
                        </a>
                      ) : (
                        <HighlightText text={portalText} search={search} filterTerm={debouncedFilters.f_PortalSource} />
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ ...tdStyle, color: COLORS.muted }}>
                    No data found for current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ ...cardStyle, marginTop: 12 }}>
          <Pagination page={page} totalPages={resp.totalPages} onPage={setPage} loading={loading} />
        </div>
      </div>
    </Layout>
  );
}
